import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  AccountTree as TreeIcon,
  People as PeopleIcon,
  PhotoLibrary as PhotoIcon,
  Add as AddIcon,
  Feedback as FeedbackIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Warning as WarningIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '../contexts/AuthContext';
import { DashboardService, QuickStats } from '../services/dashboardService';
import { WelcomeCard } from '../components/dashboard/WelcomeCard';
import { QuickStatsCard } from '../components/dashboard/QuickStatsCard';
import { RecentActivityCard } from '../components/dashboard/RecentActivityCard';
import { QuickLinksCard } from '../components/dashboard/QuickLinksCard';
import { FamilyHighlightsCard } from '../components/dashboard/FamilyHighlightsCard';
import { OnboardingModal } from '../components/dashboard/OnboardingModal';
import { GettingStartedChecklist } from '../components/dashboard/GettingStartedChecklist';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Dashboard Component
 *
 * Main landing page for authenticated users
 *
 * Features:
 * - Personalized welcome with user info
 * - Quick statistics (people, relationships, photos, generations)
 * - Quick action cards for common tasks
 * - Recent activity feed
 * - Family highlights (incomplete profiles, photos, birthdays)
 * - Role-specific sections (admin, editor, contributor, viewer)
 * - Onboarding flow for new users
 * - Getting started checklist
 *
 * NO MORE /demo REDIRECTS - All links go to real pages!
 */
const Dashboard: React.FC = () => {
  // ===== State =====
  const { authState } = useAuth();
  const user = useUser();
  const navigate = useNavigate();
  const [stats, setStats] = useState<QuickStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // ===== Effects =====
  useEffect(() => {
    fetchDashboardData();
    checkOnboarding();
  }, []);

  // ===== Helper Functions =====
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const quickStats = await DashboardService.getQuickStats();
      setStats(quickStats);
    } catch (err: any) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkOnboarding = () => {
    // Show onboarding for new users (can check user.onboardingComplete flag)
    // For now, we'll check if it's stored in localStorage
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (!hasSeenOnboarding && user && user.isApproved) {
      setShowOnboarding(true);
    }
  };

  const isNewUser = (): boolean => {
    // Consider user "new" if they have less than 3 people in database
    return stats ? stats.totalPeople < 3 : false;
  };

  // ===== Event Handlers =====
  const handleOnboardingClose = async (dontShowAgain: boolean) => {
    setShowOnboarding(false);
    if (dontShowAgain) {
      localStorage.setItem('hasSeenOnboarding', 'true');
      // Also save to Firestore user document
      if (authState.user) {
        try {
          await updateDoc(doc(db, 'users', authState.user.uid), {
            onboardingComplete: true,
          });
        } catch (error) {
          console.error('Failed to update onboarding status:', error);
        }
      }
    }
  };

  const handleViewTree = () => {
    navigate('/tree');
  };

  const handleBrowsePeople = () => {
    navigate('/people');
  };

  const handleAddPerson = () => {
    navigate('/people/add');
  };

  const handleUploadPhotos = () => {
    navigate('/people'); // Photos can be uploaded from people profiles
  };

  const handleSubmitFeedback = () => {
    navigate('/feedback/submit');
  };

  const handleMyRequests = () => {
    navigate('/feedback/my-requests');
  };

  const handleAdminPanel = () => {
    navigate('/admin');
  };

  const handleFeedbackDashboard = () => {
    navigate('/dev/feedback-dashboard');
  };

  const handleChangeApproval = () => {
    navigate('/admin/change-approval');
  };

  const handleContentModeration = () => {
    navigate('/admin/content');
  };

  // ===== Render Loading =====
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={120} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // ===== Render Error =====
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load dashboard: {error}
          <Button onClick={fetchDashboardData} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  // ===== Render =====
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Onboarding Modal */}
      <OnboardingModal
        open={showOnboarding}
        onClose={handleOnboardingClose}
      />

      {/* Welcome Card */}
      {user && <WelcomeCard user={user} />}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <QuickStatsCard
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            value={stats?.totalPeople || 0}
            label="Family Members"
            subtitle="Total people in tree"
            color="primary"
            onClick={handleBrowsePeople}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickStatsCard
            icon={<TreeIcon sx={{ fontSize: 40 }} />}
            value={stats?.totalRelationships || 0}
            label="Relationships"
            subtitle="Family connections"
            color="success"
            onClick={handleViewTree}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickStatsCard
            icon={<PhotoIcon sx={{ fontSize: 40 }} />}
            value={stats?.totalPhotos || 0}
            label="Photos"
            subtitle="Shared memories"
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickStatsCard
            icon={<TreeIcon sx={{ fontSize: 40 }} />}
            value={stats?.generations || 0}
            label="Generations"
            subtitle="Family history span"
            color="info"
          />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column: Quick Actions & Highlights */}
        <Grid item xs={12} md={8}>
          {/* Getting Started Checklist (for new users) */}
          {isNewUser() && (
            <Box sx={{ mb: 3 }}>
              <GettingStartedChecklist />
            </Box>
          )}

          {/* Quick Actions */}
          <Typography variant="h5" component="h2" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* View Family Tree */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={handleViewTree}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <TreeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    View Family Tree
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Explore your family connections
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Browse People */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={handleBrowsePeople}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <PeopleIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Browse People
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Search and view family members
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Add Family Member */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={handleAddPerson}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <AddIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Add Family Member
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add a new person to the tree
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Upload Photos */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={handleUploadPhotos}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <PhotoIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Upload Photos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Share family photos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Submit Feedback */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={handleSubmitFeedback}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <FeedbackIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Submit Feedback
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Report bugs or request features
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* My Requests */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={handleMyRequests}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <FeedbackIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    My Requests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View your submitted requests
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Admin-Specific Cards */}
            {user?.role === 'admin' && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={handleAdminPanel}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <SettingsIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Admin Panel
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manage users and system
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={handleFeedbackDashboard}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <DashboardIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Feedback Dashboard
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manage feature requests
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={handleChangeApproval}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <WarningIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Pending Changes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review and approve changes
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={handleContentModeration}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <FlagIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        Content Moderation
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review flagged content
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>

          {/* Family Highlights */}
          <FamilyHighlightsCard />
        </Grid>

        {/* Right Column: Recent Activity (Admin) or Quick Links (Others) */}
        <Grid item xs={12} md={4}>
          {user?.role === 'admin' ? <RecentActivityCard /> : <QuickLinksCard />}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
